/* eslint-disable max-len */
<template>
  <div
    id="app"
    :class="{
      'root-router-view': !v2RootRouterView,
      'v2-root-router-view': v2RootRouterView,
      'sirius-xm-footer': displaySiriusXMBanner && isSiriusXmInfoRetrieved,
      'credit-disclaimer-footer': creditDisclaimerFooterExists,
    }"
  >
    <div id="app-inner">
      <MobialisLoadingOverlay
        :class="{ secondary: isBuildAndPrice }"
        :isLoading="getLoadingBoolean || !getIsInitialized"
      />
      <transition appear name="fade">
        <div v-if="getShowingCloseDialog">
          <div class="modal-backdrop fade show"></div>
          <ClosePopup @stay="stayInModal" @go="closeDialog" />
        </div>
      </transition>
      <mobialis-header-bar shadow :class="[headerStyleClasses, { 'mb-2 mb-sm-4': v2Header }]">
        <NavButtonChanger></NavButtonChanger>
        <div v-if="!isKijijiDealer && getIsInitialized" class="nav-logo-container">
          <img
            :src="require('@/assets/av-logo.svg')"
            class="av-logo mb-1"
            alt="AutoVerify Logo"
            width="108"
            height="18"
          />
        </div>
        <button
          v-if="!hideCloseBtn"
          class="nav-button border-0 btn-light btn-lg"
          :aria-label="$t('message.common.close')"
          @click="clickCloseBtn"
          data-test="appCloseBtn"
          v-av-analytics-cta-interaction="{
            elementText: 'close',
            eventActionResult: isOnlySpin ? 'exit' : 'popup',
            elementSubtype: 'nav_icon',
          }"
        >
          <img alt="" src="@/assets/close-button.svg" />
        </button>
      </mobialis-header-bar>
      <router-view
        v-if="getIsInitialized"
        class="router-view"
        :class="{
          'container-fluid': !v2RootRouterView,
          'mb-0': noBottomSpace && !displaySiriusXMBanner,
        }"
      />
    </div>
    <SiriusXMBanner v-if="displaySiriusXMBanner"></SiriusXMBanner>
    <VehicleInfoFooter v-if="displayVehicleInfoFooter"></VehicleInfoFooter>
    <component v-if="!noFooter" class="footer-component" :is="footerComponent"></component>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import { modalMethods } from '@/mixins';
import NavButtonChanger from '@/components/NavButtonChanger.vue';
import ClosePopup from '@/components/ClosePopup.vue';
import SiriusXMBanner from '@/components/SiriusXMBanner.vue';
import VehicleInfoFooter from '@/components/credit/VehicleInfoFooter.vue';
import V2Footer from '@/components/V2Footer.vue';
import log from '@/services/log';
import { AVRProducts, AVAnalyticsEventTriggers, DHSState } from '@/utils/enums';
import helperFunctions from '@/utils/helperFunctions';

const GetReportFooter = () => import('@/modules/spotlight/layout/GetReportFooter.vue');

export default {
  mixins: [modalMethods],
  data() {
    return {};
  },
  async created() {
    const {
      vin,
      purchasePrice,
      key,
      // eslint-disable-next-line camelcase
      external_id,
      make,
      model,
      trim,
      consumerId,
    } = this.$route.query;

    this.$store.commit('user/SET_LANGUAGE', this.$i18n.locale);

    if (consumerId) {
      this.$store.commit('lead/SET_CONSUMER_ID', consumerId);
    }

    try {
      await this.$store.dispatch('business/getInfo', { route: this.$route });
    } catch (error) {
      log('Get business info error', error, {
        key,
        external_id,
      });
      if (this.isEcomV2) {
        this.$router.push('e-commerce-v2/error');
      } else if (this.isBuildAndPrice) {
        this.$router.push('build-and-price/error');
      } else {
        this.$router.push('/error');
      }
    }

    this.$store.dispatch('analytics/initShiftDigital');

    if (this.isKijijiDealer) {
      const htmlElement = document.documentElement;
      htmlElement.setAttribute('theme', this.enums.Themes.KIJIJI);
    }

    // Force the route param country if the country code is missing
    if (this.getThisIsAmerica && !this.$route.params.country) {
      this.$router.replace({
        name: this.$route.name,
        params: { country: 'us' },
      });
    }

    // Is VIN or Purchase price included?
    try {
      if (!this.VOIVinDecodeDisabled) {
        await this.$store.dispatch('vehicle/decodeVin', {
          vin,
          make,
          model,
          trim,
        });
      } else {
        this.$store.commit('vehicle/SET_VEHICLE_INFO', null);
      }
    } catch (error) {
      // Vin decoder failed
      // eslint-disable-next-line no-console
      console.warn(`No vehicle found from VIN-decoder / VIN: ${vin}`);
    } finally {
      // Ecommerce v2 requires vin & purchase price both
      if (this.isEcomV2) {
        if (this.vehicleString === null || !purchasePrice || !vin) {
          this.$router.push('e-commerce-v2/error');
        }
      }
    }

    // Add event listeners
    window.addEventListener('beforeunload', this.beforeWindowUnload);
    window.addEventListener('message', this.processParentMessage);
    window.addEventListener('popstate', this.resetVehicleData);

    // Check heart beat on user interaction
    window.addEventListener('mousemove', this.checkHeartBeat);
    window.addEventListener('scroll', this.checkHeartBeat);
    window.addEventListener('keydown', this.checkHeartBeat);
    window.addEventListener('resize', this.checkHeartBeat);
    window.addEventListener('touchmove', this.checkHeartBeat);

    // Add listeners for all input events for analytics tracking
    window.addEventListener('input', this.inputEventListener);
    window.addEventListener('change', this.inputChangeEventListener);

    this.$store.commit('SET_IS_INITIALIZED', true);
    this.$store.commit('SET_ENTRY_PRODUCT_NAME', this.getCurrentProduct);

    // Send a post message to a parent to get autoverify data
    window.parent.postMessage('appInitialized', '*');

    // send initial page view event
    helperFunctions.sendAVAnalyticsEvent(this.$route.path, AVAnalyticsEventTriggers.PAGE_VIEW, this.$store);

    this.$store.dispatch('lead/setECIDAndRegionID');

    if (this.nativeMobileIntegration) {
      this.$store.dispatch('metrics/logWidgetInteraction');
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
    window.removeEventListener('mousemove', this.checkHeartBeat);
    window.removeEventListener('scroll', this.checkHeartBeat);
    window.removeEventListener('keydown', this.checkHeartBeat);
    window.removeEventListener('resize', this.checkHeartBeat);
    window.removeEventListener('touchmove', this.checkHeartBeat);
    window.removeEventListener('popstate', this.resetVehicleData);
    window.removeEventListener('input', this.inputEventListener);
    window.removeEventListener('change', this.inputChangeEventListener);
  },
  mounted() {
    document.documentElement.classList.add('overflow-scrolling');
    document.body.classList.add('overflow-scrolling');
  },
  components: {
    NavButtonChanger,
    ClosePopup,
    V2Footer,
    GetReportFooter,
    SiriusXMBanner,
    VehicleInfoFooter,
  },
  computed: {
    ...mapGetters([
      'getLoadingBoolean',
      'getShowingCloseDialog',
      'getCurrentProduct',
      'getProductHomeState',
      'getIsInitialized',
      'nativeMobileIntegration',
      'nativeMobileDeepLink',
    ]),
    ...mapGetters('lead', ['leadIsActive']),
    ...mapGetters('business', [
      'getSubscribedProducts',
      'getThisIsAmerica',
      'showSiriusXMBanner',
      'isSiriusXmInfoRetrieved',
      'isKijijiDealer',
      'displaySelectVehicleTypePage',
    ]),
    ...mapGetters('vehicle', ['vehicleString']),
    ...mapState('lead', ['heartBeatSentTime', 'heartBeatSending']),
    ...mapState('vehicle', ['vehicleInfo']),
    ...mapState(['entryProductName']),
    ...mapState({
      VOIVinDecodeDisabled: state => state.business.info.integrationSettings.VOIVinDecodeDisabled,
    }),
    isEcomV2() {
      return this.getCurrentProduct === AVRProducts.ECOMV2;
    },
    isCredit() {
      return this.getCurrentProduct === AVRProducts.CREDIT;
    },
    isSpin() {
      return this.getCurrentProduct === AVRProducts.SPIN;
    },
    isSpotlight() {
      return this.getCurrentProduct === AVRProducts.SPOTLIGHT;
    },
    isBuildAndPrice() {
      return this.getCurrentProduct === AVRProducts.BUILD_AND_PRICE;
    },
    headerStyleClasses() {
      return this.$route.meta.headerStyleClasses || 'mb-4';
    },
    noBottomSpace() {
      return this.getCurrentProduct === AVRProducts.REVIEWS;
    },
    v2Header() {
      return this.isEcomV2 || this.isSpin || this.isBuildAndPrice;
    },
    noFooter() {
      return this.isBuildAndPrice || this.displayVehicleInfoFooter;
    },
    v2RootRouterView() {
      return this.isEcomV2 || this.isSpotlight || this.isSpin || this.isBuildAndPrice;
    },
    footerComponent() {
      return this.hasSpotlightGetReportFooter ? 'GetReportFooter' : 'v2Footer';
    },
    displaySiriusXMBanner() {
      return (
        !this.displayVehicleInfoFooter &&
        this.vehicleInfo &&
        this.vehicleInfo.vin &&
        this.showSiriusXMBanner &&
        ![AVRProducts.SPOTLIGHT, AVRProducts.ECOMV2, AVRProducts.BUILD_AND_PRICE].includes(this.getCurrentProduct)
      );
    },
    displayVehicleInfoFooter() {
      return this.$route.meta && this.$route.meta.displayVehicleInfoFooter;
    },
    hideCloseBtn() {
      return !!(this.hideAllCloseBtns || (this.$route.meta && this.$route.meta.hideHeaderCloseButton));
    },
    isOnlySpin() {
      return this.getSubscribedProducts.length === 1 && this.getSubscribedProducts.includes(AVRProducts.SPIN);
    },
    creditDisclaimerFooterExists() {
      return this.$route.path.includes('/credit/disclaimer');
    },
  },
  methods: {
    ...mapMutations({ showCloseModal: 'SET_CLOSING' }),
    inputEventListener() {
      helperFunctions.sendAVAnalyticsEvent(this.$route.path, AVAnalyticsEventTriggers.INTERACTION, this.$store);
    },
    inputChangeEventListener(event) {
      try {
        const { target } = event;
        const { id } = target;
        let elementText;
        // For some form elements, such as the confirm identity credit form,
        // we don't want to capture the real values for privacy reasons
        // Check the 10 parent elements for a data attribute for the value to use instead
        let parent = target;
        let count = 0;
        while (parent && count < 10 && !parent.getAttribute('data-av-analytics-element-text-value')) {
          parent = parent.parentElement;
          count += 1;
        }
        const found = parent && count < 10;
        if (found) {
          elementText = parent.getAttribute('data-av-analytics-element-text-value');
        } else {
          const labelElement = document.querySelector(`label[for='${CSS.escape(id)}']`);
          elementText = labelElement ? labelElement.innerText || labelElement.ariaLabel : null;
          if (!elementText && event.target.type === 'radio') {
            elementText = event.target.value;
          }
        }
        // applying cloneDeep to this.$route causes type errors so use route object in the store
        const route = cloneDeep(this.$store.state.route);
        this.$store.dispatch('analytics/formEngagementEvent', {
          elementText: elementText?.substr(0, 40),
          route,
        });
      } catch (error) {
        log('Error sending form engagement event', error);
      }
    },
    async closeDialog() {
      await this.closeModal(false).catch(error => log('Error completing lead before modal close.', error));

      if (
        this.nativeMobileIntegration &&
        this.nativeMobileDeepLink instanceof URL &&
        this.entryProductName in this.getProductHomeState
      ) {
        let productComplete;
        const queryParameter = `${this.entryProductName}_complete`;
        const productState = this.getProductHomeState[this.entryProductName].state;
        switch (this.entryProductName) {
          case 'credit':
            productComplete = (
              productState === DHSState.COMPLETE || productState === DHSState.HALF_COMPLETE
            ).toString();
            break;
          default:
            productComplete = (productState === DHSState.COMPLETE).toString();
        }
        this.nativeMobileDeepLink.searchParams.append(queryParameter, productComplete);
        window.location.assign(this.nativeMobileDeepLink.toString());
      }
    },
    stayInModal() {
      this.showCloseModal(false);
    },
    async clickCloseBtn() {
      if (this.isOnlySpin) {
        await this.closeDialog();
      } else {
        await this.showCloseDialog().catch(error => log('Error updating lead after trying to close modal', error));
      }
    },
    async processParentMessage(event) {
      if (!event.data) {
        return;
      }
      const { data } = event;
      let eventData = null;

      // Parse the data object if it's built with JSON.stringify
      try {
        eventData = JSON.parse(data);
      } catch (e) {
        eventData = data;
      }

      if (eventData === 'closeModal') {
        await this.showCloseDialog().catch(error =>
          log('Error updating lead after receiving close message from SDK.', error)
        );
      }

      // SpinCar Specific event (Not sure if this is is still in use)
      if (eventData === 'exitFullScreenMode') {
        // We just complete lead
        await this.closeModal(true).catch(error => log('Error completing lead before modal close.', error));
      }

      if (eventData.utm) {
        this.$store.commit('lead/SET_UTM_DATA', eventData.utm);
      }

      if (eventData.googleClientId) {
        this.$store.commit('lead/SET_GOOGLE_CLIENT_ID', eventData.googleClientId);
      }

      if (eventData.customData) {
        this.$store.commit('lead/SET_CUSTOM_DEALER_LEAD_DATA', eventData.customData);
      }

      if (helperFunctions.isStructuredPostMessageFromSDK(eventData) && eventData.eventType === 'windowReadyState') {
        this.$store.commit('SET_INITIALIZED_BY_SDK', true);
      }
    },
    // Check to see if we should send a heartbeat
    // If we have a lead, it's not complete or expired
    // and if has been longer than 1 minute since last heart beat was sent, send another
    checkHeartBeat() {
      if (this.leadIsActive) {
        const dateNow = new Date();
        const date1MinuteAgo = new Date().setMinutes(dateNow.getMinutes() - 1);
        if ((this.heartBeatSentTime < date1MinuteAgo || !this.heartBeatSentTime) && !this.heartBeatSending) {
          this.sendHeartBeat();
        }
      }
    },
    resetVehicleData() {
      // if we are navigating backwards (header back button or browser back button)
      // we need to reset the vehicle data in some cases for the trade,
      // acquire and insurance flows
      const { from, name } = this.$store.state.route;
      if (name === 'vehicle-autocomplete-desktop') {
        this.$store.commit('trade/RESET_VEHICLE');
      }
      if (
        from.name === 'trade-style-mobile' ||
        from.name === 'trade-style-embed' ||
        from.name === 'acquire-style-embed'
      ) {
        this.$store.commit('trade/SET_VEHICLE_STYLE', '');
      }
      if (from.name === 'trade-trim-mobile' || from.name === 'trade-trim-embed' || from.name === 'acquire-trim-embed') {
        this.$store.commit('trade/SET_VEHICLE_TRIM', '');
      }
      if (
        from.name === 'trade-model-mobile' ||
        from.name === 'trade-model-embed' ||
        from.name === 'acquire-model-embed'
      ) {
        this.$store.commit('trade/SET_VEHICLE_MODEL', '');
      }
      if (from.name === 'trade-make-mobile' || from.name === 'trade-make-embed' || from.name === 'acquire-make-embed') {
        this.$store.commit('trade/SET_VEHICLE_MAKE', '');
      }
      if (from.name === 'trade-year-mobile' || from.name === 'trade-year-embed' || from.name === 'acquire-year-embed') {
        this.$store.commit('trade/SET_VEHICLE_YEAR', '');
      }
    },
    async sendHeartBeat() {
      try {
        this.$store.commit('lead/SET_HEART_BEAT_SENDING', true);
        await this.$store.dispatch('lead/sendHeartBeat');
        this.$store.commit('lead/SET_HEART_BEAT_SENT_TIME', new Date());
      } catch (err) {
        await this.$store.dispatch('analytics/leadCompleteEvent');
        if (err?.response?.data?.errors?.leadIsExpired === true) {
          this.$store.commit('lead/SET_LEAD_EXPIRED', true);
        } else {
          log('Error sending heartbeat', err);
        }
        if (this.isEcomV2) {
          this.$router.push(`${this.getProductBasePath()}/lead-expired`);
        } else {
          this.$router.push({ name: `lead-expired-${this.baseRoute}` });
        }
      } finally {
        this.$store.commit('lead/SET_HEART_BEAT_SENDING', false);
      }
    },
    beforeWindowUnload() {
      // Try to complete the lead when the browser unloads (fires on close tab, browser or page refresh)
      // based on timing and device/browsers this might not always work
      // seems to work on all major browsers on desktop, AVR modal and embedded iframe
      // disabled on mobile since it is not supported (does not fire)
      if (this.leadIsActive && !this.isAVRMobile) {
        this.$store.dispatch('lead/completeLead');
      }
    },
  },
};
</script>

<style lang="scss" src="@/assets/scss/index.scss"></style>
